import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Calendar, Check, X, DollarSign, History } from "lucide-react";
import { format, addMonths } from "date-fns";
import { ChitPool, Member } from "@/lib/types";
import { ChitPayment, getPaymentsForMember, recordPayment, deletePayment } from "@/lib/paymentApi";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PaymentTrackerProps {
  member: Member;
  chitPool: ChitPool;
  onPaymentUpdate: () => void;
}

const PaymentTracker = ({ member, chitPool, onPaymentUpdate }: PaymentTrackerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [payments, setPayments] = useState<ChitPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingMonth, setProcessingMonth] = useState<number | null>(null);
  const { toast } = useToast();

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await getPaymentsForMember(member.id);
      // Filter payments for the current chit pool only
      const filteredPayments = data.filter(p => p.chit_pool_id === chitPool.id);
      setPayments(filteredPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast({
        title: "Error",
        description: "Failed to load payment history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPayments();
    }
  }, [isOpen, member.id]);

  const handleMarkPaid = async (monthNumber: number, amount: number) => {
    try {
      setProcessingMonth(monthNumber);
      await recordPayment(
        chitPool.id,
        member.id,
        monthNumber,
        amount,
        new Date()
      );
      toast({
        title: "Success",
        description: `Payment recorded for month ${monthNumber}`,
      });
      fetchPayments();
      onPaymentUpdate();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast({
        title: "Error",
        description: "Failed to record payment",
        variant: "destructive",
      });
    } finally {
      setProcessingMonth(null);
    }
  };

  const handleRemovePayment = async (payment: ChitPayment) => {
    try {
      setProcessingMonth(payment.month_number);
      await deletePayment(payment.id);
      toast({
        title: "Success",
        description: `Payment removed for month ${payment.month_number}`,
      });
      fetchPayments();
      onPaymentUpdate();
    } catch (error) {
      console.error("Error removing payment:", error);
      toast({
        title: "Error",
        description: "Failed to remove payment",
        variant: "destructive",
      });
    } finally {
      setProcessingMonth(null);
    }
  };

  const getMonthAmount = (monthNumber: number) => {
    // Check if member has lifted before this month
    const hasLiftedBeforeMonth = member.hasLifted && member.liftedDate && 
      new Date(member.liftedDate) < addMonths(new Date(chitPool.firstDueDate), monthNumber - 1);

    if (hasLiftedBeforeMonth) {
      return chitPool.postLiftAmount;
    }

    if (chitPool.sameAmountMonthly) {
      return chitPool.monthlyAmount;
    } else {
      return chitPool.monthlyAmounts?.[monthNumber - 1]?.amount || 0;
    }
  };

  const getPaymentForMonth = (monthNumber: number) => {
    return payments.find(p => p.month_number === monthNumber);
  };

  const monthsArray = Array.from({ length: chitPool.months }, (_, i) => i + 1);
  const paidMonths = payments.map(p => p.month_number);
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount_paid), 0);
  
  // Debug logging
  console.log('ChitPool months:', chitPool.months);
  console.log('MonthsArray:', monthsArray);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-1 sm:gap-2 w-full sm:w-auto"
      >
        <History className="h-4 w-4" />
        <span className="hidden sm:inline">Payment History</span>
        <span className="sm:hidden">Payments</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] sm:max-h-[80vh] flex flex-col p-4 sm:p-6">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-center sm:text-left">Payment History - {member.name}</DialogTitle>
            <DialogDescription className="text-center sm:text-left">
              Track and manage monthly payments for this member
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="monthly" className="mt-4 flex flex-col flex-1 min-h-0">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="monthly">Monthly Payments</TabsTrigger>
              <TabsTrigger value="summary">Summary</TabsTrigger>
            </TabsList>

            <TabsContent value="monthly" className="mt-4 flex-1 min-h-0">
              <ScrollArea className="h-[400px] w-full rounded-md border">
                <div className="w-full">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10">
                      <TableRow className="border-b bg-background">
                        <TableHead className="min-w-[80px]">Month</TableHead>
                        <TableHead className="hidden sm:table-cell">Due Date</TableHead>
                        <TableHead className="min-w-[90px]">Amount</TableHead>
                        <TableHead className="hidden md:table-cell">Status</TableHead>
                        <TableHead className="hidden lg:table-cell">Payment Date</TableHead>
                        <TableHead className="text-right min-w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthsArray.map((monthNumber) => {
                        const payment = getPaymentForMonth(monthNumber);
                        const dueDate = addMonths(new Date(chitPool.firstDueDate), monthNumber - 1);
                        const amount = getMonthAmount(monthNumber);
                        const isPaid = !!payment;

                        return (
                          <TableRow key={monthNumber} className="border-b bg-background hover:bg-muted/50">
                            <TableCell className="font-medium">
                              <div>
                                <div>Month {monthNumber}</div>
                                <div className="sm:hidden text-xs text-muted-foreground">
                                  {format(dueDate, "dd MMM")}
                                </div>
                                <div className="md:hidden">
                                  {isPaid ? (
                                    <Badge variant="default" className="gap-1 mt-1 text-xs">
                                      <Check className="h-2 w-2" />
                                      Paid
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="gap-1 mt-1 text-xs">
                                      <X className="h-2 w-2" />
                                      Pending
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {format(dueDate, "dd MMM yyyy")}
                            </TableCell>
                            <TableCell>
                              ₹{amount.toLocaleString()}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {isPaid ? (
                                <Badge variant="default" className="gap-1">
                                  <Check className="h-3 w-3" />
                                  Paid
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1">
                                  <X className="h-3 w-3" />
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {payment ? format(new Date(payment.payment_date), "dd MMM yyyy") : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              {isPaid ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemovePayment(payment)}
                                  disabled={processingMonth === monthNumber}
                                  className="text-xs sm:text-sm"
                                >
                                  {processingMonth === monthNumber ? (
                                    <span className="h-3 w-3 sm:h-4 sm:w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                  ) : (
                                    "Remove"
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleMarkPaid(monthNumber, amount)}
                                  disabled={processingMonth === monthNumber}
                                  className="text-xs sm:text-sm"
                                >
                                  {processingMonth === monthNumber ? (
                                    <span className="h-3 w-3 sm:h-4 sm:w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                  ) : (
                                    <>
                                      <span className="hidden sm:inline">Mark Paid</span>
                                      <span className="sm:hidden">Paid</span>
                                    </>
                                  )}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="summary" className="mt-4 overflow-y-auto">
              <div className="grid gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Months</span>
                      <span className="font-medium">{chitPool.months}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Months Paid</span>
                      <span className="font-medium text-green-600">{paidMonths.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Months Pending</span>
                      <span className="font-medium text-amber-600">
                        {chitPool.months - paidMonths.length}
                      </span>
                    </div>
                    <div className="pt-3 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Paid</span>
                        <span className="text-lg font-bold text-primary">
                          ₹{totalPaid.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Member Status</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Has Lifted</span>
                      <Badge variant={member.hasLifted ? "default" : "outline"}>
                        {member.hasLifted ? "Yes" : "No"}
                      </Badge>
                    </div>
                    {member.hasLifted && member.liftedDate && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Lifted Date</span>
                        <span className="font-medium">
                          {format(new Date(member.liftedDate), "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {member.disbursedAmount !== null && member.disbursedAmount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Disbursed Amount</span>
                        <span className="font-medium">
                          ₹{member.disbursedAmount.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentTracker;